import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import { MINT_MIST } from '../../constants/brandSurfaces';

const blue = '#0d4efe';
const teal = '#01ebb2';
const ink = '#1a1a2e';
const muted = '#5a6478';
const deepTeal = '#047857';

/** Chosen trade's color, set as `--trade` on Panel (brand blue until a trade is picked). */
const trade = `var(--trade, ${blue})`;
const tint = (percent) => `color-mix(in srgb, ${trade} ${percent}%, #ffffff)`;

export const Panel = styled('section')({
  position: 'relative',
  marginTop: 12,
  marginBottom: 48,
  padding: '44px 48px 48px',
  borderRadius: 24,
  background: MINT_MIST,
  border: '1px solid rgba(26, 26, 46, 0.06)',
  textAlign: 'left',
  '@media (max-width: 900px)': {
    padding: '32px 28px 36px',
  },
  '@media (max-width: 600px)': {
    marginBottom: 32,
    padding: '24px 16px 24px',
    borderRadius: 18,
  },
});

export const Inner = styled('div')({
  maxWidth: 680,
  margin: '0 auto',
});

export const LoadingCover = styled('div')({
  position: 'absolute',
  inset: 0,
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 'inherit',
  background: 'rgba(242, 248, 245, 0.88)',
});

export const Badge = styled('p')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  margin: '0 0 14px',
  padding: '6px 12px',
  borderRadius: 999,
  fontSize: '0.86rem',
  fontWeight: 600,
  color: deepTeal,
  background: 'rgba(1, 235, 178, 0.16)',
  '& svg': { fontSize: 18 },
});

export const Heading = styled('h1')({
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  fontSize: '2.3rem',
  fontWeight: 700,
  lineHeight: 1.15,
  letterSpacing: '-0.02em',
  color: ink,
  margin: '0 0 10px',
  '& em': { fontStyle: 'normal', color: blue },
  '@media (max-width: 600px)': {
    fontSize: '1.7rem',
  },
});

export const Subheading = styled('p')({
  fontSize: '1.05rem',
  lineHeight: 1.55,
  color: muted,
  margin: '0 0 18px',
  maxWidth: 560,
});

export const QuotaPill = styled('p')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  margin: '0 0 20px',
  padding: '6px 14px',
  borderRadius: 999,
  fontSize: '0.9rem',
  color: muted,
  background: '#ffffff',
  border: '1px solid rgba(26, 26, 46, 0.08)',
  '& strong': { color: ink, fontWeight: 700 },
  '&::before': {
    content: '""',
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: teal,
    boxShadow: '0 0 0 3px rgba(1, 235, 178, 0.2)',
  },
});

export const FormCard = styled('form')({
  padding: '24px 24px 26px',
  borderRadius: 20,
  background: '#ffffff',
  border: '1px solid rgba(26, 26, 46, 0.06)',
  boxShadow: '0 8px 28px rgba(26, 26, 46, 0.08)',
  '@media (max-width: 600px)': {
    padding: '18px 16px 20px',
    borderRadius: 16,
  },
});

export const FieldLabel = styled('label')({
  display: 'block',
  margin: '0 0 10px',
  fontSize: '1rem',
  fontWeight: 700,
  color: ink,
});

export const FieldGroup = styled('div')({
  '& + &': { marginTop: 22 },
});

export const TradeGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
  gap: 8,
  '@media (max-width: 640px)': {
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  },
});

export const TradeOption = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== '$color',
})(({ $color }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 6,
  padding: '12px 6px',
  borderRadius: 12,
  fontFamily: 'inherit',
  fontSize: '0.86rem',
  fontWeight: 600,
  lineHeight: 1.25,
  textAlign: 'center',
  color: ink,
  background: '#f7fafc',
  border: '1.5px solid rgba(26, 26, 46, 0.08)',
  transition: 'border-color 0.15s ease, background 0.15s ease',
  '& svg': { fontSize: 24, color: $color },
  '&:hover, &.Mui-focusVisible': {
    borderColor: $color,
    background: '#ffffff',
  },
  '&[aria-pressed="true"]': {
    borderColor: $color,
    background: `color-mix(in srgb, ${$color} 10%, #ffffff)`,
  },
}));

export const DescriptionInput = styled('textarea')({
  display: 'block',
  width: '100%',
  minHeight: 96,
  padding: '12px 14px',
  borderRadius: 12,
  border: '1.5px solid rgba(26, 26, 46, 0.12)',
  background: '#f7fafc',
  fontFamily: 'inherit',
  fontSize: '1rem',
  lineHeight: 1.5,
  color: ink,
  resize: 'vertical',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s ease, background 0.15s ease',
  '&::placeholder': { color: '#8a93a6' },
  '&:focus': {
    outline: 'none',
    background: '#ffffff',
    borderColor: trade,
    boxShadow: `0 0 0 3px ${tint(18)}`,
  },
  '&[aria-invalid="true"]': { borderColor: '#d32f2f' },
});

export const FieldHint = styled('p', {
  shouldForwardProp: (prop) => prop !== '$error',
})(({ $error }) => ({
  margin: '6px 2px 0',
  fontSize: '0.82rem',
  color: $error ? '#d32f2f' : muted,
}));

export const SubmitButton = styled(Button)({
  width: '100%',
  marginTop: 22,
  textTransform: 'none',
  borderRadius: 10,
  padding: '12px 24px',
  fontSize: '1rem',
  fontWeight: 700,
  color: '#ffffff',
  background: `linear-gradient(90deg, ${teal} 0%, ${blue} 100%)`,
  boxShadow: '0 4px 14px rgba(13, 78, 254, 0.35)',
  '&:hover': {
    background: `linear-gradient(90deg, ${teal} 0%, ${blue} 100%)`,
    filter: 'brightness(1.05)',
    boxShadow: '0 6px 18px rgba(13, 78, 254, 0.4)',
  },
  '&.Mui-disabled': {
    color: '#ffffff',
    opacity: 0.5,
  },
});

export const LimitCard = styled('div')({
  padding: '28px 24px',
  borderRadius: 20,
  background: '#ffffff',
  border: '1px solid rgba(26, 26, 46, 0.06)',
  boxShadow: '0 8px 28px rgba(26, 26, 46, 0.08)',
});

export const LimitTitle = styled('h2')({
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  fontSize: '1.3rem',
  fontWeight: 700,
  color: ink,
  margin: '0 0 8px',
});

export const LimitText = styled('p')({
  fontSize: '1rem',
  lineHeight: 1.6,
  color: muted,
  margin: '0 0 18px',
});

export const SecondaryButton = styled(Button)({
  textTransform: 'none',
  borderRadius: 10,
  padding: '10px 22px',
  fontSize: '0.95rem',
  fontWeight: 700,
  color: ink,
  border: '1px solid rgba(26, 26, 46, 0.16)',
  background: '#ffffff',
  '&:hover': {
    background: '#ffffff',
    borderColor: blue,
    color: blue,
  },
});
